<template>
<div class="d-flex pl20 pr20">
  <div style="width: 100%">
    <div
      v-if="!isWhiteGloveVariant"
      class="d-flex align-center justify-space-between gap10"
    >
      <div class="all-tools-label" style="flex: 1;">{{ site ? site.name : '' }}</div>
      <div>
        <Google-docs-folder-url-button
          v-if="site && isWhiteGloveUser"
          :site="site"
        />
      </div>
    </div>

    <div class="new-tool-root" :class="{ 'mt-10': !isWhiteGloveVariant }">
      <pages-card :key="`pages-${site?.id}`" :start-here="startHere === 'onpage'"></pages-card>
      <ai-writers-card v-if="canAccessAIWriter" :key="`ai-writers-${site?.id}`" :start-here="startHere === 'aiwriter'"></ai-writers-card>
      <keyword-research-card
        v-if="showKeywordTool"
        :key="keywordResearchCardKey"
        :is-white-glove-variant="isWhiteGloveVariant"
        :start-here="startHere === 'keyword'"
      ></keyword-research-card>
      <keyword-research-card-locked v-else :start-here="startHere === 'keyword'"></keyword-research-card-locked>
      <ai-visibility-tool-card v-if="!$store.getters.isDemoUser" :key="`ai-visibility-${site?.id}`" :start-here="startHere === 'aivis'"></ai-visibility-tool-card>
      <content-orders-card v-if="isWhiteGloveUser && !isWhiteGloveVariant" :key="`content-orders-${site?.id}`"></content-orders-card>
      <!-- <div class="tool-card pa-5"></div> -->
    </div>

  </div>
</div>
</template>

<script>
import { mixin } from '@/components/mixin'

import PagesCard from "@/components/SitesSingleView/Tools/PagesCard.vue";
import AiWritersCard from "@/components/SitesSingleView/Tools/AiWritersCard.vue";
import KeywordResearchCard from "@/components/SitesSingleView/Tools/KeywordResearchCard.vue";
import KeywordResearchCardLocked from "@/components/SitesSingleView/Tools/KeywordResearchCardLocked.vue";
import ContentOrdersCard from '@/components/SitesSingleView/Tools/ContentOrdersCard.vue';
import AiVisibilityToolCard from '@/components/AiVisibility/card/AiVisibilityToolCard.vue';
import { getPureServicePkgName } from '@/utils'
import {
  SUBSCRIPTIONS_WITH_ACCESS_TO_KEYWORD_RESEARCH_TOOLS,
  whiteGlovePlans
} from "@/components/plans_constants";
import GoogleDocsFolderUrlButton from '@/components/SitesSingleView/Tools/GoogleDocsFolderUrlButton.vue';

export default {
  mixins: [ mixin ],
  props: {
    isWhiteGloveVariant: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    PagesCard,
    AiWritersCard,
    KeywordResearchCard,
    KeywordResearchCardLocked,
    ContentOrdersCard,
    AiVisibilityToolCard,
    GoogleDocsFolderUrlButton,
  },
  computed: {
    user () {
      if (this.$store.state.user) {
        return this.$store.state.user
      }
      return null
    },
    isSpecialAdminUser () {
      return this.$store.getters.isSpecialAdminUser
    },
    isSpecialOwnerSelected () {
      return this.$store.getters.isSpecialOwnerSelected
    },
    selectedSubAccountRelation () {
      return this.$store.state.selectedSubAccountRelation
    },
    isSubAccountRelationSelected() {
      return !!this.$store.state.selectedSubAccountRelation;
    },
    isMissionControlSession () {
      return this.$store.state.selectedSubAccountRelation?.isMissionControlSubAccount
    },
    canAccessAIWriter () {
      if (
        this.isAdmin
        || this.isMissionControlSession
        || !this.isSubAccountRelationSelected
        || this?.selectedSubAccountRelation?.allowAccessToAiWriter
      ) {
        return true
      }
      return false
    },
    isWhiteGloveUser () {
      if (!this.user) return false;

      const accountType = this.$store.state.selectedSubAccountRelation
        ?this.$store.state.selectedSubAccountRelation.ownerAccountType
        :this.user.accountType

      if (whiteGlovePlans.includes(getPureServicePkgName(accountType))) {
        return true;
      }
      return false;
    },
    showKeywordTool () {
      if (this.isSpecialAdminUser || this.isSpecialOwnerSelected) {
        return true;
      }
      if (this.selectedSubAccountRelation && SUBSCRIPTIONS_WITH_ACCESS_TO_KEYWORD_RESEARCH_TOOLS.map(x => getPureServicePkgName(x)).includes(getPureServicePkgName(this.selectedSubAccountRelation.ownerAccountType))) {
        return true
      }
      if (!this.selectedSubAccountRelation && this.user && SUBSCRIPTIONS_WITH_ACCESS_TO_KEYWORD_RESEARCH_TOOLS.map(x => getPureServicePkgName(x)).includes(getPureServicePkgName(this.user.accountType))) {
        return true
      }
      return false;
    },
    keywordResearchCardKey () {
      const siteKey = this.site?.id ?? `r-${this.$route.params.siteId}`
      const rel = this.selectedSubAccountRelation
      const subKey = rel && typeof rel === 'object' && rel.id != null ? rel.id : 'main'
      return `${siteKey}-${subKey}`
    },
  },
  created () {
    if (this.isWhiteGloveUser && !this.isWhiteGloveVariant) {
      this.$router.replace(`/sites/${this.$route.params.siteId}/overview`)
      return
    }
    this.readStartHere()
  },
  methods: {
    // ?startHere=<tool> (and ?openReport=1) come from the onboarding modal: keep the highlight in
    // local state and drop the query so back / refresh do not re-highlight.
    readStartHere () {
      const query = { ...(this.$route.query || {}) }
      if (!query.startHere) return
      this.startHere = String(query.startHere)
      const openReport = query.openReport === '1'
      delete query.startHere
      delete query.openReport
      this.$router.replace({ path: this.$route.path, query }).catch(() => {})
      if (openReport) this.openExampleReport()
    },
    // No-plan accounts: the sample report opens on its own so the "Run this on your page" box shows.
    openExampleReport () {
      const example = this.$store.getters.exampleProject
      if (!example || String(example.siteId) !== String(this.$route.params.siteId) || !example.pageId || !example.pageRunId) return
      setTimeout(() => {
        this.$router.push(`/sites/${example.siteId}/pages/${example.pageId}/pageruns/${example.pageRunId}`)
      }, 350)
    },
  },
  data () {
    return {
      startHere: '', // onpage | aiwriter | keyword | aivis
    }
  }
}
</script>


<style lang="scss">
@import "./index.scss";
</style>
